/**
 * useSessionTimeout Hook
 * 
 * Implements session security with two timeout mechanisms:
 * 1. Inactivity timeout (30 min) - resets on user activity
 * 2. Absolute session max (24 hours) - forces re-auth regardless of activity
 * 
 * Industry standard:
 * - E-commerce: 30-60 min inactivity
 * - Banking: 5-15 min (stricter)
 * - SaaS: 1-4 hours
 * 
 * For Heseda AI (skincare consultation):
 * - 30 min inactivity timeout
 * - 24 hour absolute session max
 */

import { useEffect, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../services/firebaseConfig';

/** Session timeout configuration */
export interface SessionTimeoutConfig {
  /** Inactivity timeout in milliseconds (default: 30 min) */
  inactivityTimeout?: number;
  /** Absolute session max in milliseconds (default: 24 hours) */
  absoluteSessionMax?: number;
  /** Callback when session expires */
  onTimeout?: () => void;
  /** Callback when absolute session expires (optional, defaults to onTimeout) */
  onAbsoluteTimeout?: () => void;
  /** Show warning before timeout (seconds before) */
  warningBeforeTimeout?: number;
  /** Callback for warning */
  onWarning?: (secondsRemaining: number) => void;
}

const DEFAULT_INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const DEFAULT_ABSOLUTE_SESSION_MAX = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_WARNING_BEFORE = 60; // 1 minute warning
const SESSION_START_KEY = 'heseda-ai_session_start';

/**
 * Hook to manage session timeout based on user inactivity
 * 
 * @param user - Current Firebase user (null if not logged in)
 * @param config - Timeout configuration
 * 
 * @example
 * ```tsx
 * const { resetTimer, remainingTime } = useSessionTimeout(user, {
 *   inactivityTimeout: 30 * 60 * 1000, // 30 min
 *   onTimeout: () => toast.info('Session expired'),
 *   onWarning: (sec) => toast.warning(`Session expires in ${sec}s`),
 * });
 * ```
 */
export function useSessionTimeout(
  user: User | null,
  config: SessionTimeoutConfig = {}
) {
  const {
    inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT,
    absoluteSessionMax = DEFAULT_ABSOLUTE_SESSION_MAX,
    onTimeout,
    onAbsoluteTimeout,
    warningBeforeTimeout = DEFAULT_WARNING_BEFORE,
    onWarning,
  } = config;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const warningRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const absoluteTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastActivityRef = useRef<number>(Date.now());
  const sessionStartRef = useRef<number>(Date.now());
  // Track if we've logged initialization to avoid console spam
  const hasLoggedInit = useRef<boolean>(false);

  /**
   * Get or initialize session start time from localStorage
   * This persists across page refreshes but resets on new login
   */
  const getSessionStart = useCallback((): number => {
    if (!user) return Date.now();
    
    const stored = localStorage.getItem(SESSION_START_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    // New session - store the start time
    const now = Date.now();
    localStorage.setItem(SESSION_START_KEY, now.toString());
    return now;
  }, [user]);

  /**
   * Clear session start on logout
   */
  const clearSessionStart = useCallback(() => {
    localStorage.removeItem(SESSION_START_KEY);
  }, []);

  /**
   * Handle session timeout - sign out user (inactivity)
   */
  const handleTimeout = useCallback(async () => {
    console.log('⏰ Session timeout - signing out due to inactivity');
    clearSessionStart();
    
    try {
      await auth.signOut();
      onTimeout?.();
    } catch (error) {
      console.error('Error during session timeout signout:', error);
    }
  }, [onTimeout, clearSessionStart]);

  /**
   * Handle absolute session expiry - force re-auth after 24 hours
   */
  const handleAbsoluteTimeout = useCallback(async () => {
    console.log('🔒 Absolute session expired - forcing re-authentication after 24 hours');
    clearSessionStart();
    
    try {
      await auth.signOut();
      // Use specific callback if provided, otherwise fall back to onTimeout
      (onAbsoluteTimeout ?? onTimeout)?.();
    } catch (error) {
      console.error('Error during absolute session timeout signout:', error);
    }
  }, [onTimeout, onAbsoluteTimeout, clearSessionStart]);

  /**
   * Reset the inactivity timer (called on user activity)
   */
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }

    // Only set timers if user is logged in
    if (!user) return;

    lastActivityRef.current = Date.now();

    // Set warning timer (if callback provided)
    if (onWarning && warningBeforeTimeout > 0) {
      const warningTime = inactivityTimeout - (warningBeforeTimeout * 1000);
      if (warningTime > 0) {
        warningRef.current = setTimeout(() => {
          onWarning(warningBeforeTimeout);
        }, warningTime);
      }
    }

    // Set main timeout
    timeoutRef.current = setTimeout(handleTimeout, inactivityTimeout);
  }, [user, inactivityTimeout, warningBeforeTimeout, onWarning, handleTimeout]);

  /**
   * Get remaining time until timeout (for UI display)
   */
  const getRemainingTime = useCallback((): number => {
    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = Math.max(0, inactivityTimeout - elapsed);
    return Math.ceil(remaining / 1000); // seconds
  }, [inactivityTimeout]);

  /**
   * Set up activity listeners and absolute session timer
   */
  useEffect(() => {
    if (!user) {
      // Clear timers when user logs out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (absoluteTimeoutRef.current) clearTimeout(absoluteTimeoutRef.current);
      clearSessionStart();
      return;
    }

    // Initialize session start time
    const sessionStart = getSessionStart();
    sessionStartRef.current = sessionStart;

    // Check if absolute session has already expired (e.g., after page refresh)
    const sessionAge = Date.now() - sessionStart;
    if (sessionAge >= absoluteSessionMax) {
      console.log('🔒 Session already expired on page load');
      handleAbsoluteTimeout();
      return;
    }

    // Set absolute session timeout (doesn't reset on activity)
    const remainingAbsolute = absoluteSessionMax - sessionAge;
    absoluteTimeoutRef.current = setTimeout(handleAbsoluteTimeout, remainingAbsolute);
    // Only log on first initialization (avoid spam from re-renders)
    if (!hasLoggedInit.current) {
      console.log(`🔐 Absolute session expires in ${Math.round(remainingAbsolute / 1000 / 60)} minutes`);
    }

    // Activity events that reset the inactivity timer
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove',
      'click',
    ];

    // Throttle the reset to avoid excessive calls
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const throttledReset = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        resetTimer();
      }, 1000); // Throttle to once per second
    };

    // Add listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer start
    resetTimer();

    // Only log on first initialization (avoid spam from re-renders)
    if (!hasLoggedInit.current) {
      console.log(`🔐 Session timeout initialized: ${inactivityTimeout / 1000 / 60} min inactivity, ${absoluteSessionMax / 1000 / 60 / 60}h absolute max`);
      hasLoggedInit.current = true;
    }

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (absoluteTimeoutRef.current) clearTimeout(absoluteTimeoutRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [user, resetTimer, inactivityTimeout, absoluteSessionMax, getSessionStart, handleAbsoluteTimeout, clearSessionStart]);

  /**
   * Get remaining time until absolute session expiry
   */
  const getAbsoluteRemainingTime = useCallback((): number => {
    const sessionAge = Date.now() - sessionStartRef.current;
    const remaining = Math.max(0, absoluteSessionMax - sessionAge);
    return Math.ceil(remaining / 1000); // seconds
  }, [absoluteSessionMax]);

  return {
    /** Manually reset the inactivity timer (e.g., after API call) */
    resetTimer,
    /** Get remaining seconds until inactivity timeout */
    getRemainingTime,
    /** Get remaining seconds until absolute session expiry */
    getAbsoluteRemainingTime,
    /** Last activity timestamp */
    lastActivity: lastActivityRef.current,
    /** Session start timestamp */
    sessionStart: sessionStartRef.current,
  };
}

export default useSessionTimeout;
